import { observer } from 'mobx-react-lite';
import { SVGPureIcon } from '@/ui';
import { request } from '@/common';
import { useState, useEffect, useMemo } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { WorkItem } from '@/components/modal/work-detail/store';
import { formatTime } from '@q/solib-utils';

const WorkList = observer<{
  defaultList: any[];
  onClose: () => void;
  onSelect: (img: any, isChecked: boolean, index, id) => void;
}>(({ defaultList, onClose, onSelect }) => {
  const [imgList, setImgList] = useState<WorkItem[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [npt, setNpt] = useState(0);

  // 获取日期字符串
  const getDateStr = (time: string) => {
    const today = formatTime(Date.now(), 'Y-m-d');
    const d = new Date();
    d.setDate(d.getDate() - 1);
    const yesterday = formatTime(d, 'Y-m-d');
    const date = formatTime(time, 'Y-m-d');
    if (date === today) return '今天';
    if (date === yesterday) return '昨天';
    return date;
  };

  // 使用 useMemo 计算日期分组，避免重复计算
  const dateList = useMemo(() => {
    const result: { date: string; list: WorkItem[] }[] = [];

    imgList.forEach(item => {
      const date = getDateStr(item.create_time);
      const match = result.find(d => d.date === date);
      if (match) {
        match.list.push(item);
      } else {
        result.push({
          date,
          list: [item],
        });
      }
    });

    return result;
  }, [imgList]);

  const handleLoadMore = () => {
    if (!loading) {
      getImgList();
    }
  };

  const getImgList = async () => {
    if (!hasMore || loading) return;

    setLoading(true);
    try {
      const result = await request.post(
        '/task/list?appsource=so',
        {
          status_list: [2],
          biz_type_list: ['image_image'],
          npt: `${npt}`,
          page_size: 20, // 增加每页数量提高加载效率
          sort_type_list:[101, 102, 105]
        },
        // @ts-ignore
        { dataIsJson: true }
      );

      if (!result.data?.list?.length) {
        setHasMore(false);
        return;
      }

      const newData = processImageData(result.data.list);

      if (newData.length === 0) {
        setHasMore(false);
      } else {
        setNpt(prev => prev + 1);
        setImgList(prev => [...prev, ...newData]);
      }
    } catch (error) {
      console.error('Failed to load images:', error);
    } finally {
      setLoading(false);
    }
  };
  const isChecked = (id: string) => {
    return defaultList?.findIndex(imgItem => imgItem.id === id) > -1;
  };

  // 判断是否应该禁用未选中的项
  const shouldDisableUnchecked = defaultList?.length >= 7;

  const processImageData = (list: any[]) => {
    const uniqueIds = new Set(imgList.map(item => item.id_str));
    return list.reduce((acc: WorkItem[], item) => {
      if (uniqueIds.has(item.id_str)) return acc;

      try {
        const wi: WorkItem = { ...item, dataJSON: {} };
        wi.dataJSON = JSON.parse(item.data);

        if (wi.dataJSON.out_images_w) {
          try {
            wi.dataJSON.imgList = JSON.parse(wi.dataJSON.out_images_w);
            wi.dataJSON.imgList.forEach((imgItem, index) => {
              imgItem.url_download = imgItem.url;
            });
          } catch { }
          // 确保有可用的图片
          if (wi.dataJSON.imgList?.length) {
            acc.push(wi);
          }
        }
      } catch { }

      return acc;
    }, []);
  };

  useEffect(() => {
    getImgList();
  }, []);

  return (
    <div className="flex flex-col h-full gap-20px">
      <div className="flex items-center justify-between text-14px text-[#1d21294f]">
        <div
          className="flex cursor-pointer gap-4px items-center justify-center text-[#1d212982] hover:text-[#1d2129]"
          onClick={onClose}
        >
          <SVGPureIcon name="return" size={16} />
          返回
        </div>
        <div className='mr-12px'>最多支持选择7张图片</div>
      </div>
      <div
        id="scrollable-task-list"
        className="w-full h-full mt-6px pt-6px pb-12px flex flex-col nw-scrollbar overflow-y-auto"
      >
        {imgList.length > 0 ? (
          <InfiniteScroll
            dataLength={imgList.length}
            next={handleLoadMore}
            hasMore={hasMore}
            className='work-publish-list'
            loader={
              <div className="flex items-center justify-center my-20px">
                <SVGPureIcon
                  className="size-24px animate-spin text-[rgb(0,0,0,.6)]"
                  name="loading"
                />
              </div>
            }
            endMessage={
              <div className="flex gap-20px items-center justify-center my-20px text-16px text-[rgb(0,0,0,.1)]">
                <span className="w-100px h-1px bg-[rgb(0,0,0,.1)]"></span>~
                <span className="w-100px h-1px bg-[rgb(0,0,0,.1)]"></span>
              </div>
            }
            scrollableTarget="scrollable-task-list"
          >
            {dateList.map(({ date, list }, index) => (
              <div key={index} className="mb-24px">
                <h3 className="m-0 mb-12px pl-8px text-[rgb(0,0,0,.5)] text-14px font-400">
                  {date}
                </h3>
                <ul className="grid grid-cols-4 gap-8px m-0">
                  {list.flatMap((item, i) =>
                    (item.dataJSON?.imgList || []).map(
                      (img, imgIndex) =>
                        (img.url_download || img.url) && (
                          <li
                            key={`${item.id_str}-${imgIndex}`}
                            className={`relative h-88px overflow-hidden rounded-8px cursor-pointer hover:opacity-90 transition-opacity ${shouldDisableUnchecked && !isChecked(img?.b_id)
                                ? 'opacity-50 pointer-events-none'
                                : ''
                              }`}
                            onClick={() =>
                              onSelect(
                                img,
                                isChecked(img?.b_id),
                                imgIndex + 1,
                                item?.biz_id
                              )
                            }
                          >
                            {isChecked(img?.b_id) ? (
                              <div className="flex items-center justify-center absolute right-8px top-8px size-16px rounded-8px border-1px border-solid border-[#fff] bg-[#006AFF]">
                                <SVGPureIcon
                                  name="publish-checked"
                                  className="text-[#fff]"
                                  size={10}
                                />
                              </div>
                            ) : (
                              <div className="absolute right-8px top-8px size-16px rounded-8px border-1px border-solid border-[#fff]"></div>
                            )}
                            <img
                              src={img.url_download || img.url}
                              alt=""
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </li>
                        )
                    )
                  )}
                </ul>
              </div>
            ))}
          </InfiniteScroll>
        ) : (
          <>
            {loading ? (
              <div className="flex items-center justify-center flex-grow">
                <SVGPureIcon
                  className="size-48px animate-spin text-[rgb(0,0,0,.6)]"
                  name="loading"
                />
              </div>
            ) : (
              <div className="flex items-center justify-center flex-col flex-grow">
                <img
                  src="https://p2.ssl.qhimg.com/t110b9a9301e3bc97ae108c572e.png"
                  className="w-300px h-140px"
                />
                <span className="text-14px text-[rgb(0,0,0,.3)]">
                  什么都没有～
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
});

export default WorkList;
